/* eslint-disable max-lines */
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import {
  Globe,
  Clock,
  Webhook,
  Mail,
  Save,
  HardDrive,
  AlertTriangle,
  Settings,
  Server,
  Cpu,
  Database,
  CheckCircle2,
  XCircle,
  AlertCircle
} from 'lucide-react';
import PageHeader from '@components/layout/page-header';
import Button from '@components/atoms/ui-kit/button';
import { Skeleton } from '@components/atoms/ui-kit/skeleton';
import { useUpdateAdminSettings } from '@utils/api/admin/use-update-admin-settings';
import { useGetAdminSettings } from '@utils/api/admin/use-get-admin-settings';
import { useGetAdminSystem } from '@utils/api/admin/use-get-admin-system';
import { ServiceHealth } from '@utils/types/admin';

type TabId = 'general' | 'system-info';

/**
 * On-Prem Admin Settings Page
 * Contains two tabs: General settings and System Info
 */
export default function AdminSettings() {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const [searchParams] = useSearchParams();
  const updateSettingsMutation = useUpdateAdminSettings();

  // Initialize tab from URL query param (e.g., ?tab=system)
  const getInitialTab = (): TabId => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'system') return 'system-info';
    return 'general';
  };

  const [activeTab, setActiveTab] = useState<TabId>(getInitialTab);

  // Form state for general settings
  const [defaultLanguage, setDefaultLanguage] = useState('en');
  const [retentionDays, setRetentionDays] = useState('365');
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maxUploadSize, setMaxUploadSize] = useState('500');

  // Fetch current settings from API
  const { data: settingsData, isLoading: settingsLoading } =
    useGetAdminSettings();
  const settings = settingsData?.result;

  // Initialize form state from API data
  useEffect(() => {
    if (settings) {
      setDefaultLanguage(settings.default_language || 'en');
      setRetentionDays(settings.retention_days_default || '365');
      setMaintenanceMode(settings.maintenance_mode === 'true');
      setMaxUploadSize(settings.max_upload_size_mb || '500');
    }
  }, [settings]);

  // System info data
  const { data: systemData, isLoading: systemLoading } = useGetAdminSystem();
  const systemInfo = systemData?.result;

  const handleSave = () => {
    updateSettingsMutation.mutate({
      default_language: defaultLanguage,
      retention_days_default: retentionDays,
      maintenance_mode: maintenanceMode ? 'true' : 'false',
      max_upload_size_mb: maxUploadSize
    });
  };

  const getStatusDisplay = (status: string) => {
    switch (status) {
      case 'healthy':
        return {
          icon: CheckCircle2,
          iconStyle: { color: 'rgb(34,197,94)' },
          bgStyle: { backgroundColor: 'rgba(34,197,94,0.1)' },
          badgeStyle: {
            backgroundColor: 'rgba(34,197,94,0.1)',
            color: 'rgb(22,163,74)'
          }
        };
      case 'degraded':
        return {
          icon: AlertCircle,
          iconStyle: { color: 'rgb(245,158,11)' },
          bgStyle: { backgroundColor: 'rgba(245,158,11,0.1)' },
          badgeStyle: {
            backgroundColor: 'rgba(245,158,11,0.1)',
            color: 'rgb(217,119,6)'
          }
        };
      case 'unhealthy':
        return {
          icon: XCircle,
          iconStyle: { color: 'rgb(239,68,68)' },
          bgStyle: { backgroundColor: 'rgba(239,68,68,0.1)' },
          badgeStyle: {
            backgroundColor: 'rgba(239,68,68,0.1)',
            color: 'rgb(220,38,38)'
          }
        };
      default:
        return {
          icon: AlertCircle,
          iconStyle: { color: 'rgb(107,114,128)' },
          bgStyle: { backgroundColor: 'rgba(107,114,128,0.1)' },
          badgeStyle: {
            backgroundColor: 'rgba(107,114,128,0.1)',
            color: 'rgb(107,114,128)'
          }
        };
    }
  };

  // Format bytes to human readable
  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const tabs: { id: TabId; label: string; icon: typeof Settings }[] = [
    { id: 'general', label: t('general'), icon: Settings },
    { id: 'system-info', label: t('system-info'), icon: Server }
  ];

  return (
    <div className="bg-app min-h-screen">
      <PageHeader
        title={t('settings')}
        caption={t('configure-system-settings-and-defaults')}
      />

      {/* Tabs */}
      <div
        className="flex gap-1 mb-6 border-b"
        style={{ borderColor: getColour('border') }}
      >
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors"
            style={{
              color:
                activeTab === tab.id
                  ? getColour('activeText')
                  : getColour('inactiveText'),
              borderBottom:
                activeTab === tab.id
                  ? `2px solid ${getColour('activeText')}`
                  : '2px solid transparent'
            }}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* General Settings Tab */}
      {activeTab === 'general' &&
        (settingsLoading ? (
          <Skeleton variant="card" />
        ) : (
          <div className="space-y-2">
            {/* Row 1: Language Defaults + Retention Policy */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {/* Language Defaults */}
              <section
                className="rounded-2xl border p-6"
                style={{
                  borderColor: getColour('border'),
                  backgroundColor: getColour('secondLayer')
                }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <Globe
                    className="h-5 w-5"
                    style={{ color: getColour('inactiveText') }}
                  />
                  <h2
                    className="text-sm font-semibold"
                    style={{ color: getColour('normalText') }}
                  >
                    {t('language-defaults')}
                  </h2>
                </div>
                <div>
                  <label
                    className="block text-xs font-medium uppercase tracking-wide mb-2"
                    style={{ color: getColour('inactiveText') }}
                  >
                    {t('default-language')}
                  </label>
                  <select
                    className="w-full px-3 py-2 rounded-2xl text-sm"
                    style={{
                      backgroundColor: getColour('thirdLayer'),
                      border: `1px solid ${getColour('border')}`,
                      color: getColour('normalText')
                    }}
                    value={defaultLanguage}
                    onChange={e => setDefaultLanguage(e.target.value)}
                  >
                    <option value="en">English (en)</option>
                    <option value="nl">Dutch (nl)</option>
                    <option value="de">German (de)</option>
                    <option value="fr">French (fr)</option>
                  </select>
                </div>
              </section>

              {/* Retention Policy */}
              <section
                className="rounded-2xl border p-6"
                style={{
                  borderColor: getColour('border'),
                  backgroundColor: getColour('secondLayer')
                }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <Clock
                    className="h-5 w-5"
                    style={{ color: getColour('inactiveText') }}
                  />
                  <h2
                    className="text-sm font-semibold"
                    style={{ color: getColour('normalText') }}
                  >
                    {t('retention-policy')}
                  </h2>
                </div>
                <div>
                  <label
                    className="block text-xs font-medium uppercase tracking-wide mb-2"
                    style={{ color: getColour('inactiveText') }}
                  >
                    {t('default-retention-days')}
                  </label>
                  <input
                    type="number"
                    className="w-full px-3 py-2 rounded-2xl text-sm"
                    style={{
                      backgroundColor: getColour('thirdLayer'),
                      border: `1px solid ${getColour('border')}`,
                      color: getColour('normalText')
                    }}
                    value={retentionDays}
                    onChange={e => setRetentionDays(e.target.value)}
                    min={1}
                  />
                  <p
                    className="mt-1 text-xs"
                    style={{ color: getColour('inactiveText') }}
                  >
                    {t('retention-policy-description')}
                  </p>
                </div>
              </section>
            </div>

            {/* Row 2: Upload Limits + Maintenance Mode */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {/* Upload Limits */}
              <section
                className="rounded-2xl border p-6"
                style={{
                  borderColor: getColour('border'),
                  backgroundColor: getColour('secondLayer')
                }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <HardDrive
                    className="h-5 w-5"
                    style={{ color: getColour('inactiveText') }}
                  />
                  <h2
                    className="text-sm font-semibold"
                    style={{ color: getColour('normalText') }}
                  >
                    {t('upload-limits')}
                  </h2>
                </div>
                <div>
                  <label
                    className="block text-xs font-medium uppercase tracking-wide mb-2"
                    style={{ color: getColour('inactiveText') }}
                  >
                    {t('max-upload-size-mb')}
                  </label>
                  <input
                    type="number"
                    className="w-full px-3 py-2 rounded-2xl text-sm"
                    style={{
                      backgroundColor: getColour('thirdLayer'),
                      border: `1px solid ${getColour('border')}`,
                      color: getColour('normalText')
                    }}
                    value={maxUploadSize}
                    onChange={e => setMaxUploadSize(e.target.value)}
                    min={1}
                  />
                  <p
                    className="mt-1 text-xs"
                    style={{ color: getColour('inactiveText') }}
                  >
                    {t('max-upload-size-description')}
                  </p>
                </div>
              </section>

              {/* Maintenance Mode */}
              <section
                className={`rounded-2xl border p-6 ${maintenanceMode ? 'border-amber-500/50 bg-amber-400/5 dark:bg-amber-400/10' : ''}`}
                style={
                  maintenanceMode
                    ? undefined
                    : {
                        borderColor: getColour('border'),
                        backgroundColor: getColour('secondLayer')
                      }
                }
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <AlertTriangle
                      className={`h-5 w-5 ${maintenanceMode ? 'text-amber-500 dark:text-amber-400' : ''}`}
                      style={
                        maintenanceMode
                          ? undefined
                          : { color: getColour('inactiveText') }
                      }
                    />
                    <div>
                      <h2
                        className="text-sm font-semibold"
                        style={{ color: getColour('normalText') }}
                      >
                        {t('maintenance-mode')}
                      </h2>
                      <p
                        className="text-xs"
                        style={{ color: getColour('inactiveText') }}
                      >
                        {t('maintenance-mode-description')}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setMaintenanceMode(!maintenanceMode)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${maintenanceMode ? 'bg-amber-500 border border-amber-500' : 'border'}`}
                    style={
                      maintenanceMode
                        ? undefined
                        : {
                            backgroundColor: getColour('border'),
                            borderColor: getColour('inactiveText')
                          }
                    }
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full transition-transform ${maintenanceMode ? 'translate-x-5 bg-white' : 'translate-x-1'}`}
                      style={
                        maintenanceMode
                          ? undefined
                          : { backgroundColor: getColour('normalText') }
                      }
                    />
                  </button>
                </div>
              </section>
            </div>

            {/* Row 3: Webhook Defaults + Email Configuration */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {/* Webhook Defaults */}
              <section
                className="rounded-2xl border p-6"
                style={{
                  borderColor: getColour('border'),
                  backgroundColor: getColour('secondLayer')
                }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <Webhook
                    className="h-5 w-5"
                    style={{ color: getColour('inactiveText') }}
                  />
                  <h2
                    className="text-sm font-semibold"
                    style={{ color: getColour('normalText') }}
                  >
                    {t('webhook-defaults')}
                  </h2>
                </div>
                <div>
                  <label
                    className="block text-xs font-medium uppercase tracking-wide mb-2"
                    style={{ color: getColour('inactiveText') }}
                  >
                    {t('default-webhook-url')}{' '}
                    <span className="normal-case font-normal">
                      ({t('read-only')})
                    </span>
                  </label>
                  <input
                    type="url"
                    className="w-full px-3 py-2 rounded-2xl text-sm opacity-60 cursor-not-allowed"
                    style={{
                      backgroundColor: getColour('thirdLayer'),
                      border: `1px solid ${getColour('border')}`,
                      color: getColour('normalText')
                    }}
                    placeholder="https://example.com/webhook"
                    readOnly
                  />
                </div>
              </section>

              {/* Email Configuration */}
              <section
                className="rounded-2xl border p-6"
                style={{
                  borderColor: getColour('border'),
                  backgroundColor: getColour('secondLayer')
                }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <Mail
                    className="h-5 w-5"
                    style={{ color: getColour('inactiveText') }}
                  />
                  <h2
                    className="text-sm font-semibold"
                    style={{ color: getColour('normalText') }}
                  >
                    {t('email-configuration')}
                  </h2>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <label
                      className="block text-xs font-medium uppercase tracking-wide mb-2"
                      style={{ color: getColour('inactiveText') }}
                    >
                      {t('smtp-host')}
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 rounded-2xl text-sm"
                      style={{
                        backgroundColor: getColour('thirdLayer'),
                        border: `1px solid ${getColour('border')}`,
                        color: getColour('normalText')
                      }}
                      placeholder="mail.example.com"
                      readOnly
                    />
                  </div>
                  <div>
                    <label
                      className="block text-xs font-medium uppercase tracking-wide mb-2"
                      style={{ color: getColour('inactiveText') }}
                    >
                      {t('smtp-port')}
                    </label>
                    <input
                      type="number"
                      className="w-full px-3 py-2 rounded-2xl text-sm"
                      style={{
                        backgroundColor: getColour('thirdLayer'),
                        border: `1px solid ${getColour('border')}`,
                        color: getColour('normalText')
                      }}
                      defaultValue={587}
                      readOnly
                    />
                  </div>
                  <div className="lg:col-span-2">
                    <label
                      className="block text-xs font-medium uppercase tracking-wide mb-2"
                      style={{ color: getColour('inactiveText') }}
                    >
                      {t('from-address')}
                    </label>
                    <input
                      type="email"
                      className="w-full px-3 py-2 rounded-2xl text-sm"
                      style={{
                        backgroundColor: getColour('thirdLayer'),
                        border: `1px solid ${getColour('border')}`,
                        color: getColour('normalText')
                      }}
                      placeholder="noreply@example.com"
                      readOnly
                    />
                  </div>
                </div>
                <p
                  className="mt-4 text-xs"
                  style={{ color: getColour('inactiveText') }}
                >
                  {t('email-configuration-note')}
                </p>
              </section>
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-2">
              <Button
                onClick={handleSave}
                disabled={updateSettingsMutation.isLoading}
                loading={updateSettingsMutation.isLoading}
                variant="primary"
                size="small"
                icon={<Save className="h-4 w-4" />}
                label={t('save-changes')}
              />
            </div>
          </div>
        ))}

      {/* System Info Tab */}
      {activeTab === 'system-info' && (
        <>
          {systemLoading ? (
            <Skeleton variant="card" />
          ) : !systemInfo ? (
            <div
              className="rounded-2xl border p-8 text-center"
              style={{
                borderColor: getColour('border'),
                backgroundColor: getColour('secondLayer')
              }}
            >
              <Server
                className="h-12 w-12 mx-auto mb-4"
                style={{ color: getColour('inactiveText') }}
              />
              <p
                className="text-sm"
                style={{ color: getColour('inactiveText') }}
              >
                {t('unable-to-load-system-info')}
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Version Info */}
              <section
                className="rounded-2xl border p-6"
                style={{
                  borderColor: getColour('border'),
                  backgroundColor: getColour('secondLayer')
                }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <Server
                    className="h-5 w-5"
                    style={{ color: getColour('inactiveText') }}
                  />
                  <h2
                    className="text-sm font-semibold"
                    style={{ color: getColour('normalText') }}
                  >
                    {t('version-information')}
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label
                      className="block text-xs font-medium uppercase tracking-wide mb-1"
                      style={{ color: getColour('inactiveText') }}
                    >
                      {t('version')}
                    </label>
                    <span
                      className="text-sm font-mono"
                      style={{ color: getColour('normalText') }}
                    >
                      {systemInfo.version}
                    </span>
                  </div>
                  <div>
                    <label
                      className="block text-xs font-medium uppercase tracking-wide mb-1"
                      style={{ color: getColour('inactiveText') }}
                    >
                      {t('k8s-namespace')}
                    </label>
                    <span
                      className="text-sm font-mono"
                      style={{ color: getColour('normalText') }}
                    >
                      {systemInfo.k8s_namespace || '-'}
                    </span>
                  </div>
                  <div>
                    <label
                      className="block text-xs font-medium uppercase tracking-wide mb-1"
                      style={{ color: getColour('inactiveText') }}
                    >
                      {t('storage-driver')}
                    </label>
                    <span
                      className="text-sm font-mono"
                      style={{ color: getColour('normalText') }}
                    >
                      {systemInfo.storage_driver}
                    </span>
                  </div>
                </div>
              </section>

              {/* Scriptix model info */}
              <section
                className="rounded-2xl border p-6"
                style={{
                  borderColor: getColour('border'),
                  backgroundColor: getColour('secondLayer')
                }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <Cpu
                    className="h-5 w-5"
                    style={{ color: getColour('inactiveText') }}
                  />
                  <h2
                    className="text-sm font-semibold"
                    style={{ color: getColour('normalText') }}
                  >
                    {t('whisper-model')}
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label
                      className="block text-xs font-medium uppercase tracking-wide mb-1"
                      style={{ color: getColour('inactiveText') }}
                    >
                      {t('model')}
                    </label>
                    <span
                      className="text-sm"
                      style={{ color: getColour('normalText') }}
                    >
                      {systemInfo.whisper_model}
                    </span>
                  </div>
                  <div>
                    <label
                      className="block text-xs font-medium uppercase tracking-wide mb-1"
                      style={{ color: getColour('inactiveText') }}
                    >
                      {t('compute-type')}
                    </label>
                    <span
                      className="text-sm"
                      style={{ color: getColour('normalText') }}
                    >
                      {systemInfo.whisper_compute_type || '-'}
                    </span>
                  </div>
                  <div>
                    <label
                      className="block text-xs font-medium uppercase tracking-wide mb-1"
                      style={{ color: getColour('inactiveText') }}
                    >
                      {t('gpu-enabled')}
                    </label>
                    <span
                      className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full"
                      style={
                        systemInfo.gpu_enabled
                          ? {
                              backgroundColor: 'rgba(34,197,94,0.1)',
                              color: 'rgb(22,163,74)'
                            }
                          : {
                              backgroundColor: 'rgba(107,114,128,0.1)',
                              color: 'rgb(107,114,128)'
                            }
                      }
                    >
                      {systemInfo.gpu_enabled ? t('yes') : t('no')}
                    </span>
                  </div>
                </div>
              </section>

              {/* Storage Info */}
              <section
                className="rounded-2xl border p-6"
                style={{
                  borderColor: getColour('border'),
                  backgroundColor: getColour('secondLayer')
                }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <HardDrive
                    className="h-5 w-5"
                    style={{ color: getColour('inactiveText') }}
                  />
                  <h2
                    className="text-sm font-semibold"
                    style={{ color: getColour('normalText') }}
                  >
                    {t('storage')}
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label
                      className="block text-xs font-medium uppercase tracking-wide mb-1"
                      style={{ color: getColour('inactiveText') }}
                    >
                      {t('used')}
                    </label>
                    <span
                      className="text-sm"
                      style={{ color: getColour('normalText') }}
                    >
                      {formatBytes(systemInfo.storage_used_bytes)}
                    </span>
                  </div>
                  <div>
                    <label
                      className="block text-xs font-medium uppercase tracking-wide mb-1"
                      style={{ color: getColour('inactiveText') }}
                    >
                      {t('total')}
                    </label>
                    <span
                      className="text-sm"
                      style={{ color: getColour('normalText') }}
                    >
                      {formatBytes(systemInfo.storage_total_bytes)}
                    </span>
                  </div>
                  <div>
                    <label
                      className="block text-xs font-medium uppercase tracking-wide mb-1"
                      style={{ color: getColour('inactiveText') }}
                    >
                      {t('available')}
                    </label>
                    <span
                      className="text-sm"
                      style={{ color: getColour('normalText') }}
                    >
                      {formatBytes(
                        systemInfo.storage_total_bytes -
                          systemInfo.storage_used_bytes
                      )}
                    </span>
                  </div>
                </div>
                {/* Storage progress bar */}
                <div className="mt-4">
                  <div
                    className="h-2 rounded-full overflow-hidden"
                    style={{ backgroundColor: getColour('thirdLayer') }}
                  >
                    {(() => {
                      const ratio =
                        systemInfo.storage_total_bytes > 0
                          ? systemInfo.storage_used_bytes /
                            systemInfo.storage_total_bytes
                          : 0;
                      const fillClass =
                        ratio > 0.9
                          ? 'bg-red-500 dark:bg-red-400'
                          : ratio > 0.7
                            ? 'bg-amber-500 dark:bg-amber-400'
                            : 'bg-green-500 dark:bg-green-400';
                      return (
                        <div
                          className={`h-full rounded-full ${fillClass}`}
                          style={{ width: `${Math.min(ratio * 100, 100)}%` }}
                        />
                      );
                    })()}
                  </div>
                  <p
                    className="mt-1 text-xs"
                    style={{ color: getColour('inactiveText') }}
                  >
                    {Math.round(
                      systemInfo.storage_total_bytes > 0
                        ? (systemInfo.storage_used_bytes /
                            systemInfo.storage_total_bytes) *
                            100
                        : 0
                    )}
                    % {t('used')}
                  </p>
                </div>
              </section>

              {/* Services Health */}
              <section
                className="rounded-2xl border p-6"
                style={{
                  borderColor: getColour('border'),
                  backgroundColor: getColour('secondLayer')
                }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <Database
                    className="h-5 w-5"
                    style={{ color: getColour('inactiveText') }}
                  />
                  <h2
                    className="text-sm font-semibold"
                    style={{ color: getColour('normalText') }}
                  >
                    {t('services-health')}
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {systemInfo.services.map((service: ServiceHealth) => {
                    const statusDisplay = getStatusDisplay(service.status);
                    const StatusIcon = statusDisplay.icon;
                    return (
                      <div
                        key={service.name}
                        className="flex items-center justify-between p-3 rounded-2xl"
                        style={{ backgroundColor: getColour('thirdLayer') }}
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className="h-8 w-8 rounded-full flex items-center justify-center"
                            style={statusDisplay.bgStyle}
                          >
                            <StatusIcon
                              className="h-4 w-4"
                              style={statusDisplay.iconStyle}
                            />
                          </div>
                          <span
                            className="text-sm font-medium"
                            style={{ color: getColour('normalText') }}
                          >
                            {service.name}
                          </span>
                        </div>
                        <span
                          className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full capitalize"
                          style={statusDisplay.badgeStyle}
                        >
                          {t(service.status)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </section>
            </div>
          )}
        </>
      )}
    </div>
  );
}
